// frontend/src/features/ups/UpsBlockedAssignmentsList.js
// Phase 8.7B: UPS-admin exception queue for orphaned/blocked assignments.
// Reads: GET /ups/sites/:siteKey/assignments/orphaned
// Shows "blocked_reason" and allows inspection (details).
//
// Phase 8.7C:
// - Add cleanup-only actions (Complete / Cancel) for blocked/orphaned assignments.
// - After action, refetch list (reload) so resolved rows disappear.

// Phase 09 note:
// - This tab is intentionally NOT time-bucket-specific.
// - Blocked/orphaned assignments are an exception queue, not an operational lane.
// - Therefore we keep the read broad (timeBucket: undefined).
// - However, blocked rows must still preserve canonical starts_at / ends_at
//   so exception handling remains aligned with the Phase 09 time model.
// It is a cleanup and diagnosis page for UPS admins.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Eye, AlertTriangle } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import { selectConcreteSelectedSiteId, selectIsAllSitesMode, } from '../sites/sitesSlice';
import { selectFilters, selectStatsRefreshKey, bumpStatsRefreshKey } from '../ui/filtersSlice';

import DriverDetailsModal from './modals/DriverDetailsModal';
import { upsertDriver, selectDriverById } from '../drivers/driverSlice';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES, CompanyStatusBadge } from './ui';

import {
  fetchOrphanedAssignments,
  fetchCompanyOrphanedAssignments,
  completeAssignment,
  cancelAssignment,
} from '../../services/assignmentService';



const normalizeId = (v) => (v == null ? undefined : Number(v));

const BLOCKED_ASSIGNMENT_QUERY = {
  timeBucket: undefined,
};

function reasonBadgeTone(reason) {
  const r = String(reason || '').toLowerCase();
  if (r.includes('missing contract')) return 'warning';
  if (r.includes('contract disabled')) return 'warning';
  if (r.includes('site not allowed')) return 'danger';
  if (r.includes('site disabled')) return 'danger';
  return 'neutral';
}

function blockedOwnerLabel(driver) {
  return driver.workforce_source === 'COMPANY'
    ? 'Internal'
    : (driver.agency_name || driver.agency?.name || '—');
}

/**
 * Row component so we can safely call hooks (useSelector) per-row.
 * This lets us show "Banned" badge based on the canonical driver store state.
 */
function BlockedAssignmentRow({
  driver,
  openDetails,
  onComplete,
  onCancel,
  actionBusyId,
}) {
  const bannedDriver = useSelector((state) =>
    driver.id ? selectDriverById(state, driver.id) : null
  );

  // Prefer explicit boolean if you have it; otherwise swap to banned_at/ban_reason
  const isBanned = Boolean(bannedDriver?.is_banned);

  const busy = actionBusyId === String(driver.assignment_id);

  return (
    <tr
      key={driver.assignment_id ?? driver.id}
      data-row-id={String(driver.assignment_id ?? driver.id)} // assignment-first identity
      {...(driver.id ? { 'data-driver-id': String(driver.id) } : {})} // optional driver fallback
      className="transition-colors hover:bg-app-surface"
    >
      {/* Name + Banned badge */}
      <td className="px-6 py-5 whitespace-nowrap">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate font-medium text-app-text">{driver.name}</span>

          {isBanned && (
            <CompanyStatusBadge tone="banned">
              Banned
            </CompanyStatusBadge>
          )}
        </div>
      </td>

      <td className="px-6 py-5 whitespace-nowrap text-app-text">
        {driver.vehicle_registration_number || driver.vrn || '—'}
      </td>

      <td className="px-6 py-5 whitespace-nowrap text-app-text">{driver.phone || '—'}</td>
      <td className="px-6 py-5 whitespace-nowrap text-app-text">{driver.email || '—'}</td>

      <td className="px-6 py-5 whitespace-nowrap text-app-text">
      {driver.workforce_source === 'COMPANY'
        ? 'Internal'
        : (driver.agency_name || driver.agency?.name || '—')}
      </td>

      <td className="px-6 py-5 whitespace-nowrap text-app-text">
        {driver.site_name || '—'}
      </td>

      <td className="px-6 py-5 whitespace-nowrap">
        <span title={driver.blocked_reason || 'Blocked'}>
          <CompanyStatusBadge tone={reasonBadgeTone(driver.blocked_reason)}>
            {driver.blocked_reason || 'Blocked'}
          </CompanyStatusBadge>
        </span>
      </td>

      <td className="px-6 py-5 whitespace-nowrap text-app-muted">
        {driver.submission_date ? new Date(driver.submission_date).toLocaleString() : '—'}
      </td>

      <td className="px-6 py-5">
        <div className="flex flex-wrap items-center gap-2">
          {/* Details only (exception queue) */}
          <button
            onClick={() => openDetails(driver)}
            aria-label="View driver details"
            title="View details"
            className={COMPANY_ACTION_CLASSES.iconSecondary}
          >
            <Eye size={16} />
          </button>

          {/* Phase 8.7C: cleanup-only actions */}
          <button
            onClick={() => onComplete(driver)}
            disabled={busy}
            className={COMPANY_ACTION_CLASSES.compactSuccess}
            title={
              isBanned
                ? 'Driver is banned. Only mark Complete if the job already finished.'
                : 'Mark job as completed'
            }
          >
            Complete
          </button>

          <button
            onClick={() => onCancel(driver)}
            disabled={busy}
            className={COMPANY_ACTION_CLASSES.compactDestructive}
            title="Cancel (job stopped)"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

function BlockedAssignmentMobileCard({
  driver,
  openDetails,
  onComplete,
  onCancel,
  actionBusyId,
}) {
  const bannedDriver = useSelector((state) =>
    driver.id ? selectDriverById(state, driver.id) : null
  );
  const isBanned = Boolean(bannedDriver?.is_banned);
  const busy = actionBusyId === String(driver.assignment_id);
  const avatarText = (driver.name || '')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2);
  const submittedDate = driver.submission_date || driver.created_at;

  return (
    <div
      data-row-id={String(driver.assignment_id ?? driver.id)}
      {...(driver.id ? { 'data-driver-id': String(driver.id) } : {})}
      className="min-w-0"
    >
      <DriverMobileCard
        tone="blocked"
        title={driver.name || '—'}
        subtitle={`Assignment ID · ${
          driver.assignment_id ? `ASN-${driver.assignment_id}` : '—'
        }`}
        avatarText={avatarText}
        fields={[
          ['Vehicle', driver.vehicle_registration_number || driver.vrn],
          ['Owner', blockedOwnerLabel(driver)],
          ['Site', driver.site_name],
          ['Assignment Status', driver.assignment_status],
          ['Blocked Reason', driver.blocked_reason],
          ['Phone', driver.phone],
          ['Email', driver.email],
          ['Submitted', submittedDate ? new Date(submittedDate).toLocaleString() : null],
        ]}
        badges={[
          <span key="reason" title={driver.blocked_reason || 'Blocked'}>
            <CompanyStatusBadge tone={reasonBadgeTone(driver.blocked_reason)}>
              {driver.blocked_reason || 'Blocked'}
            </CompanyStatusBadge>
          </span>,
          isBanned ? (
            <CompanyStatusBadge key="banned" tone="banned">
              Banned
            </CompanyStatusBadge>
          ) : null,
        ]}
        actions={
          <>
            <button
              onClick={() => openDetails(driver)}
              aria-label="View driver details"
              title="View details"
              className={COMPANY_ACTION_CLASSES.compactSecondary}
            >
              <Eye size={16} />
            </button>

            <button
              onClick={() => onComplete(driver)}
              disabled={busy}
              className={COMPANY_ACTION_CLASSES.compactSuccess}
              title={
                isBanned
                  ? 'Driver is banned. Only mark Complete if the job already finished.'
                  : 'Mark job as completed'
              }
            >
              Complete
            </button>

            <button
              onClick={() => onCancel(driver)}
              disabled={busy}
              className={COMPANY_ACTION_CLASSES.compactDestructive}
              title="Cancel (job stopped)"
            >
              Cancel
            </button>
          </>
        }
      />
    </div>
  );
}

export default function UpsBlockedAssignmentsList() {
  const dispatch = useDispatch();

  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, source } = useSelector(selectFilters);
  const statsRefreshKey = useSelector(selectStatsRefreshKey);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);
  const selectedSiteId = useSelector(selectConcreteSelectedSiteId);


  // Local state (exception queue; keep simple)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // Phase 8.7C: disable per-row actions while a cleanup call is in flight
  const [actionBusyId, setActionBusyId] = useState(null);

  const reload = async () => {
    if (!isAdmin) return;
    if (!isAllSitesMode && !selectedSiteId) return;

    setLoading(true);
    setLoadErr(null);

    try {
      const query = {
        agencyId: agencyId ? Number(agencyId) : undefined,
        search: (q || '').trim() ? q : undefined,
        source,
        ...BLOCKED_ASSIGNMENT_QUERY,
        // backend defaults to ['requested','confirmed','active'] when not provided
      };

      const data = isAllSitesMode
        ? await fetchCompanyOrphanedAssignments(query)
        : await fetchOrphanedAssignments(Number(selectedSiteId), query);

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadErr(e?.response?.data?.error || e?.message || 'Failed to load blocked assignments');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Load orphaned rows whenever site, filters, or shared invalidation changes
  useEffect(() => {
    let alive = true;

    async function run() {
      if (!alive) return;

      // Keep state consistent when there is no site or user is not admin
        if ((!isAllSitesMode && !selectedSiteId) || !isAdmin) {
        setRows([]);
        setLoading(false);
        setLoadErr(null);
        return;
      }

      await reload();
    }

    run();
    return () => {
      alive = false;
    };
  }, [selectedSiteId, isAllSitesMode, isAdmin, agencyId, q, source, statsRefreshKey]);

  // Focus-on-arrival via ?focus=<assignmentId OR driverId> (Phase 8.7B supports both)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;

    const el =
      document.querySelector(`[data-row-id="${focus}"]`) ||
      document.querySelector(`[data-driver-id="${focus}"]`);
    if (!el) return;

    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-app-primary/40', 'animate-pulse');

    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-app-primary/40', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);

    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Adapt orphaned assignment rows into "driver-like" rows for reuse
  const drivers = useMemo(
  () =>
    (rows || []).map((r) => ({
      // driver
      id: normalizeId(r.id),
      assignment_id: normalizeId(r.assignment_id),
      name: r.name,
      vehicle_registration_number: r.vehicle_registration_number,
      vrn: r.vrn,
      phone: r.phone,
      email: r.email,
      submission_date: r.submission_date,
      agency_id: r.agency_id,
      agency_name: r.agency_name,
      workforce_source: r.workforce_source ?? null,
      company_id: normalizeId(r.company_id),
      photo_url: r.photo_url,
      photo_mime: r.photo_mime,
      photo_updated_at: r.photo_updated_at,

      // assignment metadata
      assignment_status: r.assignment_status,
      assignment_type: r.assignment_type,
      site_id: normalizeId(r.site_id),
      site_name: r.site_name ?? null,

      // Phase 09 canonical time carry-through
      starts_at: r.starts_at ?? null,
      ends_at: r.ends_at ?? null,

      // Keep legacy fields during transition
      start_time: r.start_time ?? null,
      end_time: r.end_time ?? null,

      created_at: r.created_at,

      // blocked
      blocked_reason: r.blocked_reason,
    })),
  [rows]
);

  // Empty-state filter context labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;
  // Optional UX: make empty state explicit in all-sites mode
  const siteScopeLabel = isAllSitesMode ? ' across all sites' : '';

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);

  const openDetails = (driver) => {
    dispatch(upsertDriver(driver)); // prime Redux so modal can be live-updated
    setDetailsDriver(driver);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsDriver(null);
  };

  // Cleanup actions use row.site_id so writes stay site-correct in all-sites mode.
  async function onComplete(row) {
    const aId = row.assignment_id ?? row.assignmentId ?? row.id;
    const actionSiteId = row?.site_id || selectedSiteId; // Phase 5: use row site in All Sites mode.

    if (!aId || !actionSiteId) return;

    try {
      setActionBusyId(String(aId));
      await completeAssignment(actionSiteId, aId);
      await reload(); // Keeps current read mode: site-scoped or all-sites.
      dispatch(bumpStatsRefreshKey());
    } catch (e) {
      setLoadErr('Failed to complete assignment');
    } finally {
      setActionBusyId(null);
    }
  }

  async function onCancel(row) {
    const aId = row.assignment_id ?? row.assignmentId ?? row.id;
    const actionSiteId = row?.site_id || selectedSiteId; // Phase 5: use row site in All Sites mode.

    if (!aId || !actionSiteId) return;

    try {
      setActionBusyId(String(aId));
      await cancelAssignment(actionSiteId, aId);
      await reload(); // Keeps current read mode: site-scoped or all-sites.
      dispatch(bumpStatsRefreshKey());
    } catch (e) {
      setLoadErr('Failed to cancel assignment');
    } finally {
      setActionBusyId(null);
    }
  }

  // Guard: if not admin, show a clear message (route should already be gated, but nice UX)
  if (!isAdmin) {
    return (
      <div className="my-6 space-y-4">
        <h3 className="text-2xl font-bold text-app-text">Blocked Assignments</h3>
        <div className="rounded-2xl border border-app-border bg-app-card p-6 text-app-muted shadow-sm">
          This page is restricted to UPS admins.
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="my-6 overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-app-text">Blocked Assignments</h3>
              <AlertTriangle size={18} className="text-app-accent" />
            </div>
            <p className="mt-1 text-sm text-app-muted">
              Exception queue for assignments requiring cleanup or diagnosis.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {drivers.length} blocked
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {[
                  'Driver',
                  'Vehicle',
                  'Phone',
                  'Email',
                  'Owner',
                  'Site',
                  'Reason',
                  'Submitted',
                  'Actions',
                ].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border text-sm">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-sm text-app-muted">
                    Loading blocked assignments…
                  </td>
                </tr>
              ) : loadErr ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10">
                    <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-app-card px-4 py-4 text-center text-sm font-medium text-red-700 shadow-sm">
                      {loadErr}
                    </div>
                  </td>
                </tr>
              ) : drivers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-sm text-app-muted">
                    No blocked assignments{siteScopeLabel}{agencyId ? ` for ${scopeLabel}` : ''}
                    {hasSearch ? ` matching “${q}”` : ''}.
                  </td>
                </tr>
              ) : (
                drivers.map((driver) => (
                  <BlockedAssignmentRow
                    key={driver.assignment_id ?? driver.id}
                    driver={driver}
                    openDetails={openDetails}
                    onComplete={onComplete}
                    onCancel={onCancel}
                    actionBusyId={actionBusyId}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:p-5 xl:hidden">
          {loading ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              Loading blocked assignments...
            </div>
          ) : loadErr ? (
            <div className="rounded-2xl border border-red-200 bg-app-card px-4 py-10 text-center text-sm font-medium text-red-700 shadow-sm">
              {loadErr}
            </div>
          ) : drivers.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              No blocked assignments{siteScopeLabel}{agencyId ? ` for ${scopeLabel}` : ''}
              {hasSearch ? ` matching "${q}"` : ''}.
            </div>
          ) : (
            drivers.map((driver) => (
              <BlockedAssignmentMobileCard
                key={driver.assignment_id ?? driver.id}
                driver={driver}
                openDetails={openDetails}
                onComplete={onComplete}
                onCancel={onCancel}
                actionBusyId={actionBusyId}
              />
            ))
          )}
        </div>

        <div className="border-t border-app-border px-6 py-4 text-sm text-app-muted">
          Note: Blocked rows are hidden from normal tabs because the agency has no active contract,
          or the site is not allowed by contract.
        </div>
      </section>

      {/* Driver Details Modal */}
      {showDetails && detailsDriver && (
      <DriverDetailsModal
        driver={detailsDriver}
        onClose={closeDetails}
        onMoveSuccess={reload}
        allowMoveSite={false}
      />
    )}
        </>
  );
}
